import { useEffect, useMemo, useState } from 'react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { TextInput } from '../../../shared/components/TextInput';
import { OrgUnit, OrgUnitNode, mensagemDeErro } from '../api';
import { useCentrosDeCusto, useSalvarArea } from '../hooks';
import { CadastroModal } from './CadastroModal';

const NENHUMA = '';

interface Props {
  open: boolean;
  area: OrgUnit | null;
  tree: OrgUnitNode[];
  onClose: () => void;
  onSaved: (message: string) => void;
}

/** Ids da área e de todos os seus descendentes. */
function subtreeIds(tree: OrgUnitNode[], rootId: string): Set<string> {
  const ids = new Set<string>();
  const visit = (nodes: OrgUnitNode[], inside: boolean) => {
    for (const node of nodes) {
      const isInside = inside || node.id === rootId;
      if (isInside) ids.add(node.id);
      visit(node.children, isInside);
    }
  };
  visit(tree, false);
  return ids;
}

/** Achata a árvore com o caminho completo no rótulo. */
function flatten(tree: OrgUnitNode[], prefix = ''): Array<{ node: OrgUnitNode; path: string }> {
  return tree.flatMap((node) => {
    const path = prefix ? `${prefix} › ${node.name}` : node.name;
    return [{ node, path }, ...flatten(node.children, path)];
  });
}

export function AreaFormModal({ open, area, tree, onClose, onSaved }: Props) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState(NENHUMA);
  const [costCenterId, setCostCenterId] = useState(NENHUMA);
  const salvar = useSalvarArea();
  const centros = useCentrosDeCusto(open);

  useEffect(() => {
    if (!open) return;
    setCode(area?.code ?? '');
    setName(area?.name ?? '');
    setParentId(area?.parentId ?? NENHUMA);
    setCostCenterId(area?.defaultCostCenterId ?? NENHUMA);
    salvar.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, area]);

  // A própria área, seus descendentes e áreas inativas não podem ser a
  // área superior — o backend recusaria (ciclo ou superior inativa).
  const parentOptions = useMemo(() => {
    const blocked = area ? subtreeIds(tree, area.id) : new Set<string>();
    return [
      { value: NENHUMA, label: 'Nenhuma (área raiz)' },
      ...flatten(tree)
        .filter(({ node }) => node.active && !blocked.has(node.id))
        .map(({ node, path }) => ({ value: node.id, label: path, description: node.code })),
    ];
  }, [tree, area]);

  const costCenterOptions = useMemo(
    () => [
      { value: NENHUMA, label: 'Nenhum' },
      ...(centros.data ?? []).map((c) => ({ value: c.id, label: c.name, description: c.code })),
    ],
    [centros.data],
  );

  async function handleSubmit() {
    try {
      await salvar.mutateAsync({
        id: area?.id,
        payload: {
          code: code.trim(),
          name: name.trim(),
          parentId: parentId || null,
          defaultCostCenterId: costCenterId || null,
        },
      });
      onSaved(area ? 'Área atualizada.' : 'Área cadastrada.');
    } catch {
      // Erro exibido no próprio modal.
    }
  }

  return (
    <CadastroModal
      open={open}
      title={area ? 'Editar área' : 'Nova área'}
      submitLabel="Salvar"
      isSubmitting={salvar.isPending}
      submitError={salvar.isError ? mensagemDeErro(salvar.error, 'Não foi possível salvar a área.') : null}
      canSubmit={code.trim() !== '' && name.trim() !== ''}
      onClose={onClose}
      onSubmit={handleSubmit}
    >
      <TextInput label="Código" value={code} maxLength={40} onChange={(e) => setCode(e.target.value)} />
      <TextInput label="Nome" value={name} maxLength={160} onChange={(e) => setName(e.target.value)} />
      <AutocompleteSelect label="Área superior" options={parentOptions} value={parentId} onChange={setParentId} />
      <AutocompleteSelect
        label="Centro de custo padrão"
        options={costCenterOptions}
        value={costCenterId}
        onChange={setCostCenterId}
      />
    </CadastroModal>
  );
}
